import type { Address } from "../schemas";
import type { SkuRegionPrice } from "../schemas/enums/crossBorder.enum";
import { WooCommerceOrderStatus } from "../services";
import { ShipEngineResponse } from "../services/shipEngine/types";
import { ShopifyOrderFulfillmentStatus } from "../services/shopify/types";
import { ZenkraftResponse, ZenkraftShipmentResponse, ZenkraftTrackingStage } from "../services/zenkraft/types";
import { CalendarLocale } from "./localization";
import { PromotionTemplate } from "./promotionTemplate";
import { Service, ServiceId } from "./service";

export interface ConnectionQueryParams {
  objectId?: string | null;
  type: Connection["type"];
  domain?: string | null;
}

export interface PurchaseOrderResponse {
  data: {
    orders: PurchaseOrder[];
    lastPurchaseOrder?: LastPurchaseOrderConnectionKeys;
  };
  timestamp: string;
  path: string;
}

interface BaseConnection {
  id: string;
  vendorKey: string;
  sourceServiceId: Service["id"];
  sourceObjectId: string;
  targetInstallationId: string;
  createdAt: string;
  updatedAt: string;
}

export const PurchaseOrderStatusKeys = [
  "AddressValidationError",
  "Error",
  "Canceled",
  "Delivered",
  "DeliveryError",
  "Fulfilled",
  "LabelError",
  "PartiallyDelivered",
  "PartiallyFulfilled",
  "PartiallyShipped",
  "Shipped",
  "WaitingToBeFulfilled",
  "ProductError",
  "Publishing",
  "ZenkraftAddressError",
] as const;

export type PurchaseOrderStatus = (typeof PurchaseOrderStatusKeys)[number];

// Supported cross-border corridors. Both sides are BDE-region roasters; the
// direction is determined by the vendor's physical ship-from country (NL vs DE),
// not the region code (BDE covers both NL- and DE-based roasters). Single source
// of truth — the runtime guard in cross-border-helper derives its Set from this.
export const CrossBorderDirections = ["DE_TO_NL", "NL_TO_DE"] as const;

export type CrossBorderDirection = (typeof CrossBorderDirections)[number];

export type OrderStatusCounts = Record<PurchaseOrderStatus, number>;

export type PurchaseOrderState = "ACTIVE" | "PENDING" | "CANCELLED" | "PAUSED" | "DELETED";

export interface ShippingDetails {
  shippingAmount: string;
  shippingCurrency?: string;
  shippingAddress: Address;
  //Used for audit purpose when BM updates an incorrect address through SF UI
  lastShippingAddress?: Address;
  shippingAddressLastUpdatedAt?: string;
  shippingAddressLastUpdatedBy?: string;
}

export interface PurchaseOrderConnection extends BaseConnection {
  batchId?: string;
  beanzConnectLabelPrintEnabled: boolean;
  customerAccountName?: string;
  customerAccountNumber: string;
  errorMessage?: string;
  expirationTime: number;
  identifier?: string;
  giftMessage?: string; // from d365Order.Notes.GiftMessageNote
  lineItemIds: string[];
  marketplaceObjectId?: string;
  externalOrderNumber?: string; // ConsumerNumber number D365 order
  orderAmount: string;
  orderStatus: PurchaseOrderStatus;
  orderWeightInGrams?: number;
  appliedPromotionName?: string; // Indicates if the order has a promotion applied
  purchaseOrderId: string; // Scan key
  lastTrackedAt?: string; // ISO timestamp — set after each tracking attempt (used for cooldown filtering)
  reconDate?: string;
  salesOrderId: string;
  shippingDetails: ShippingDetails;
  sourceData?: string;
  state: PurchaseOrderState;
  targetObjectId?: string; // Shopify order id, secondary index scan key
  type: "purchaseOrderConnection";
  vendorSystem: ServiceId;
  vendorSystemOrderStatus?: ShopifyOrderFulfillmentStatus | WooCommerceOrderStatus | null;
  webOrderNumber: string;
  vendorRegion: string;
  shipToCountry?: string;
  // Resolved at query time from the vendor's physical ship-from country vs the
  // order's destination country; not persisted. Present only on cross-border
  // tracking results (getCrossBorderPurchaseOrderConnections).
  crossBorderDirection?: CrossBorderDirection;
  suppressVendorSystemSyncForBlp?: boolean;
  expectedFulfillmentDate?: string; // Expected fulfillment date in UTC ISO format
  vendorCustomerKey: string; // for index to make query fast, example: ${vendorKey}#${customerAccountNumber}
  orderStatusCreatedAtKey: string; // for index to make query fast, example: ${orderStatus}#${createdAt}#${purchaseOrderId}
  openOrderVendorKey?: string; // sparse index key: the upper-cased vendorKey while the order is open (not Delivered/Canceled), absent otherwise
}

export type LineItemFulfillmentStatus =
  | "AddressValidationError"
  | "Canceled"
  | "CarrierFulfillmentError"
  | "CarrierScanned"
  | "Delivered"
  | "DeliveryError"
  | "Error"
  | "LabelCreated"
  | "LabelError"
  | "ProductError"
  | "WaitingToBeFulfilled"
  | "ZenkraftAddressError";

export interface PurchaseOrderLineItem {
  caffeinationType?: CaffeinationType; // enriched at label-creation time from the matched ProductConnection (not persisted by the order pipeline)
  canceledDate?: string;
  carrier?: string;
  carrierScanDate?: string;
  createdAt: string;
  deliveryDate?: string;
  errorMessage?: string;
  estimatedDeliveryDate?: string;
  expirationTime?: number;
  externalShippingStatus?: ZenkraftTrackingStage;
  fulfillmentStatus?: LineItemFulfillmentStatus;
  grindType: string;
  id: string; // GUID, Scan key
  labelCreatedDate?: string;
  labelData?: ZenkraftShipmentResponse;
  labelErrorDate?: string;
  labelPrintedDate?: string;
  labelUrl?: string;
  lineItemWeightInGrams?: number; // comes from productConnection, if mapping not found it can be undefnied
  marketplaceObjectId?: string;
  externalOrderNumber?: string;
  orderNumber: string; //orderNumber: SO_PO(ex:SO30611873_PO30028190)
  productTitle: string;
  variantTitle?: string;
  purchaseOrderId: string; // Partition key
  quantity: string;
  shipNoticeTriggerDate?: string;
  sourceProductSku: string; // PIM sku
  targetObjectId?: string; // Vendor system line item id
  targetProductId?: string; // Vendor system product id (ex: woocommerce id)
  trackingData?: ZenkraftResponse | ShipEngineResponse;
  trackingNumber?: string; // Secondary index partition key
  trackingUrl?: string;
  updatedAt: string;
  vendorKey: string;
  vendorSystem: ServiceId;
  type: "purchaseOrderLineItem";
}

export type GrindType = "Aero press" | "Chemex" | "Cold brew" | "Drip" | "Espresso" | "French press" | "Pour over" | "Whole bean";

export type CaffeinationType = "decaf" | "non-decaf";

export interface ProductConnectionAttributes {
  caffeinationType?: CaffeinationType;
}

export interface ProductConnection extends BaseConnection {
  type: "productConnection";
  grindType: GrindType;
  bagSize: string;
  targetProductId: string;
  targetVariantId: string; // Shopify variant id
  state: "ACTIVE" | "PAUSED" | "DELETED";
  productWeightInGrams: number;
  identifier: string;
  attributes?: ProductConnectionAttributes; // SKU-level attributes (e.g. isDecaf) — optional, no version bump
}

export type Connection = ProductConnection | PurchaseOrderConnection;
export type CreateConnectionParams = Omit<ProductConnection, "id">;

export interface ProductConnectionRequest {
  vendorId: string;
  vendorRegion: string;
  identifier: string;
  sourceObjectId: string; //PIM SKU
  bagSize: string;
  productWeightInGrams: number;
  targetMetadata: string;
  attributes?: ProductConnectionAttributes;
}

export interface TargetMetadata {
  version: string;
  targetInstallationId: string;
  targetProductId: string;
  variants: TargetVariant[];
  // Cross-border per-region prices (one entry per ship-to country). Absent for
  // non-cross-border products. See SkuRegionPrice for the derivation contract.
  skuPrices?: SkuRegionPrice[];
}

export interface TargetVariant {
  targetVariantId: string;
  grindType: GrindType;
}

export interface ProductConnectionResponse {
  message: string;
}

export interface LastPurchaseOrderConnectionKeys {
  vendorKey: string;
  purchaseOrderId: string;
}

export interface LastPurchaseOrderLineItem {
  purchaseOrderId: string;
  id: string;
}

export interface PurchaseOrderConnectionQueryResult {
  purchaseOrderConnections: PurchaseOrderConnection[];
  lastPurchaseOrderConnectionKeys?: LastPurchaseOrderConnectionKeys;
}

export interface PurchaseOrderConnectionQueryResultV1 {
  purchaseOrderConnections: PurchaseOrderConnection[];
  exclusiveStartKeyParam?: string;
}

export interface PurchaseOrderLineItemKeys {
  purchaseOrderId: string;
  id: string;
}

export interface PurchaseOrderConnectionKeys {
  vendorKey: string;
  purchaseOrderId: string;
}

export interface PurchaseOrder {
  id?: string;
  vendorKey: string;
  vendorName?: string;
  batchId?: string;
  purchaseOrderId: string;
  webOrderNumber: string;
  giftMessage?: string;
  externalOrderNumber?: string;
  salesOrderId: string;
  orderStatus?: PurchaseOrderStatus;
  vendorSystemOrderStatus?: ShopifyOrderFulfillmentStatus | WooCommerceOrderStatus | null; // null is from shopify;
  vendorSystem: string;
  targetObjectId?: string;
  targetInstallationId?: string;
  shippingAddress?: Address;
  orderTotal?: string;
  shippingAmount?: string;
  orderWeightInGrams?: number;
  lineItems: PurchaseOrderLine[];
  createdAt: string;
  errorMessage?: string;
  errors?: Error[];
  promotionTemplate?: PromotionTemplate; // Full template embedded with base64 images
  customerAccountNumber: string;
  customerAccountName: string;
  customerAccountNotes?: string[]; // Customer account notes for packing slips
  isEdiPartner?: boolean; // Whether the customer account is an EDI partner
  isInternalAccount?: boolean; // Whether the customer account is an internal account
  beanzConnectLabelPrintEnabled: boolean;
  expectedFulfillmentDate?: string;
  calendarLocale?: CalendarLocale;
  // Resolved server-side from the vendor's physical ship-from country; present
  // only on cross-border tracking results. Mirrors PurchaseOrderConnection.
  crossBorderDirection?: CrossBorderDirection;
}

export interface PurchaseOrderLine {
  id?: string;
  targetObjectId?: string; // Vendor system line item id
  sourceProductSku?: string; // PIM SKU
  quantity: string;
  carrier?: string;
  trackingNumber?: string;
  trackingUrl?: string;
  fulfillmentStatus?: LineItemFulfillmentStatus;
  lineItemWeightInGrams?: number;
  productTitle?: string;
  variantTitle?: string;
  grindType?: string;
  vendorSystem: string;
  labelErrorDate?: string;
  labelCreatedDate?: string;
  labelPrintedDate?: string;
  carrierScanDate?: string;
  errorMessage?: string;
  exportLabelUrl?: string;
}

export interface FetchOrdersRequest {
  limit: number;
  vendorKey: string;
  max?: number;
  batchId?: string;
  createdFrom?: string;
  createdTo?: string;
  orderStatus?: PurchaseOrderStatus[];
  lastPurchaseOrderId?: string;
  sortByCreatedAt?: string;
  customerAccountNumbers?: string[];
}

export interface FetchOrdersRequestV1 {
  limit?: number;
  vendorKey: string;
  batchId?: string;
  createdFrom?: string;
  createdTo?: string;
  orderStatuses?: PurchaseOrderStatus[];
  encodedExclusiveStartKey?: string;
  customerAccountNumbers?: string[];
  sortByCreatedAt?: string;
}

export interface FetchPurchaseOrderRequest {
  vendorKey: string;
  purchaseOrderId: string;
}

export interface FetchCrossBorderOrdersRequest {
  direction?: string;
  orderStatus?: PurchaseOrderStatus[];
  createdFrom?: string;
  createdTo?: string;
  limit?: number;
}

export interface CrossBorderAnalytics {
  totalOrders: number;
  byDirection: Record<string, number>;
  byStatus: Record<string, number>;
  byCarrier: Record<string, number>;
  errorCount: number;
}

export interface CrossBorderOrderSummary {
  orders: PurchaseOrder[];
  analytics: CrossBorderAnalytics;
}
